// src/documents/lorData.js
//
// Valores del paquete LOR (DFS + Rights + contrato) a partir del reclamo.
// El contrato siempre va a nombre de Luis R. Claudio (dueño de Adjustify),
// sin importar qué PA trajo el lead — decisión del cliente 2026-09-23.
//
// Los datos salen del formulario del reclamo (CreateClaimRecord.jsx):
// agreedFeePercent es el % acordado con el cliente al crear el reclamo; el
// FA usa insuranceAdjusterName/Phone/Email y el DA deskAdjusterName/Phone/Email.
import { insuredNames } from './spolData';

export const PA_PRINTED = 'Luis R. Claudio - License #W541409';

const has = v => v !== null && v !== undefined && String(v).trim() !== '';

const usDate = iso => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
  return m ? `${m[2]}/${m[3]}/${m[1]}` : '';
};

const ownerName = o => (o && (o.firstName || o.lastName) ? `${o.firstName || ''} ${o.lastName || ''}`.trim() : '');

// Dirección del asegurado en el contrato: la postal si es distinta, si no la
// de la pérdida; si solo existe la dirección vieja en texto libre, va entera
// en la calle.
const policyholderAddress = cr => {
  if (cr.mailingSameAsLoss === false && has(cr.mailingAddressStreet)) {
    return { street: cr.mailingAddressStreet, city: cr.mailingAddressCity, state: cr.mailingAddressState, zip: cr.mailingAddressZip };
  }
  if (has(cr.lossAddressStreet)) {
    return { street: cr.lossAddressStreet, city: cr.lossAddressCity, state: cr.lossAddressState, zip: cr.lossAddressZip };
  }
  return { street: cr.clientAddress || '', city: '', state: '', zip: '' };
};

const LABELS = {
  clientName: ['Asegurado', 'Insured'],
  address: ['Dirección del asegurado', 'Insured address'],
  contact: ['Teléfono o correo del cliente', 'Client phone or email'],
  insuranceCompanyName: ['Aseguradora', 'Insurance company'],
  policyNumber: ['Número de póliza', 'Policy number'],
  claimNumber: ['Número de reclamo', 'Claim number'],
  lossAddress: ['Dirección de la pérdida', 'Loss address'],
  incidentDate: ['Date of Loss', 'Date of Loss'],
  lossDescription: ['Descripción de la pérdida', 'Loss description'],
  claimType: ['Tipo de reclamo (emergencia / no emergencia / suplementario)', 'Claim type'],
  agreedFeePercent: ['% de honorario acordado', 'Agreed fee %']
};

export const buildLorData = (cr = {}, language = 'es') => {
  const address = policyholderAddress(cr);
  const lossStreet = cr.lossAddressStreet || cr.clientAddress || '';
  const fee = has(cr.agreedFeePercent) && !Number.isNaN(Number(cr.agreedFeePercent)) ? Number(cr.agreedFeePercent) : null;

  const values = {
    insuredNames: insuredNames(cr),
    insured1: cr.propertyType === 'commercial' && has(cr.commercialEntityName) ? cr.commercialEntityName : (cr.clientName || ''),
    insured2: ownerName(cr.owner2),
    clientEmail: cr.clientEmail || cr.commercialEntityEmail || '',
    addressStreet: address.street,
    addressCity: address.city,
    addressState: address.state,
    addressZip: address.zip,
    clientPhone: cr.clientPhone || cr.commercialEntityPhone || '',
    clientMobile: cr.clientMobile || '',
    insuranceCompany: cr.insuranceCompanyName || '',
    policyNumber: cr.policyNumber || '',
    claimNumber: cr.claimNumber || '',
    insuranceAddress: cr.insuranceCompanyAddress || '',
    fieldAdjuster: cr.insuranceAdjusterName || '',
    fieldAdjusterPhone: cr.insuranceAdjusterPhone || '',
    fieldAdjusterEmail: cr.insuranceAdjusterEmail || '',
    deskAdjuster: cr.deskAdjusterName || '',
    deskAdjusterPhone: cr.deskAdjusterPhone || '',
    deskAdjusterEmail: cr.deskAdjusterEmail || '',
    lossStreet,
    lossCity: cr.lossAddressCity || '',
    lossStateZip: [cr.lossAddressState, cr.lossAddressZip].filter(has).join(' '),
    dateOfLoss: usDate(cr.incidentDate),
    lossDescription: cr.lossDescription || '',
    claimTypeEmergency: cr.claimType === 'emergency' ? 'X' : '',
    claimTypeNonEmergency: cr.claimType === 'non_emergency' ? 'X' : '',
    claimTypeSupplemental: cr.claimType === 'supplemental' ? 'X' : '',
    // Sin % cargado NO se deja el 20 % de la plantilla: queda "____ %" para
    // que nunca salga un contrato con un honorario que no se acordó.
    feePercent: fee === null ? '____ %' : `${fee} %`,
    paPrinted: PA_PRINTED
  };

  const missingKeys = [
    !has(cr.clientName) && 'clientName',
    !has(address.street) && 'address',
    !has(values.clientPhone) && !has(values.clientEmail) && 'contact',
    !has(cr.insuranceCompanyName) && 'insuranceCompanyName',
    !has(cr.policyNumber) && 'policyNumber',
    !has(cr.claimNumber) && 'claimNumber',
    !has(lossStreet) && 'lossAddress',
    !values.dateOfLoss && 'incidentDate',
    !has(cr.lossDescription) && 'lossDescription',
    !has(cr.claimType) && 'claimType',
    fee === null && 'agreedFeePercent'
  ].filter(Boolean);

  const idx = language === 'es' ? 0 : 1;
  return { values, missing: missingKeys.map(key => ({ key, label: LABELS[key][idx] })) };
};
